import classNames from 'classnames';
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { Icon } from '../Icon';
import { Typography } from '../Typography';

import styles from './Input.module.css';

export type InputSize = 'sm' | 'md';

/** The designed size of the in-field error glyph, in px. */
const ERROR_ICON_SIZE = 16;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** Field label — rendered as a `<label>` bound to the input. */
  label?: ReactNode;
  /** Keep the label for assistive tech but hide it visually. */
  hideLabel?: boolean;
  /** Helper text under the field (hidden while an error message shows). */
  hintText?: ReactNode;
  /** Error message under the field — its presence also styles the field as invalid. */
  errorMessage?: ReactNode;
  /** Force the invalid styling without an error message. */
  destructive?: boolean;
  size?: InputSize;
  /** Icon inside the field, before the text. */
  leadingIcon?: ReactNode;
  /** Icon/control inside the field, after the text (e.g. a help tooltip, clear button). */
  trailingIcon?: ReactNode;
  /** Inline affix before the text — inside the focus ring (e.g. `$`, a `US ⌄` select). */
  prefix?: ReactNode;
  /** Inline affix after the text — inside the focus ring (e.g. `USD ⌄`). */
  suffix?: ReactNode;
  /** Attached segment before the field — a separate box, outside the focus ring (e.g. `http://`). */
  leadingAddon?: ReactNode;
  /** Attached segment after the field — a separate box, outside the focus ring (e.g. a Copy button). */
  trailingAddon?: ReactNode;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Extra class on the native `<input>`. */
  inputClassName?: string;
}

/**
 * Text field with label, hint / error message, size variants, and a full set of
 * add-on slots. Inline `prefix`/`suffix` and icons live inside the field and share
 * its focus ring; `leadingAddon`/`trailingAddon` are separate attached segments that
 * stay outside the ring (matching the Figma "Input field" group). Forwards its ref to
 * the native `<input>`, so it drops straight into react-hook-form or an input-mask lib.
 *
 * @interim Seeded from the Figma "Input field"; replaceable by the UI Kit without API breakage.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hideLabel = false,
    hintText,
    errorMessage,
    destructive = false,
    size = 'md',
    leadingIcon,
    trailingIcon,
    prefix,
    suffix,
    leadingAddon,
    trailingAddon,
    required,
    disabled,
    id,
    className,
    inputClassName,
    ...inputProps
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const messageId = `${inputId}-message`;
  const hasError = destructive || errorMessage != null;
  const message = errorMessage ?? hintText;
  /**
   * The invalid field's own alert glyph (Figma "Input field", error state — e.g. node `829:5162`).
   *
   * A red border is colour alone, which WCAG 1.4.1 does not accept as the only carrier of a
   * state; the glyph is the non-colour half of it. A caller that supplies its own `trailingIcon`
   * — a password visibility toggle, say — keeps it: that slot holds a control the user operates,
   * and replacing it with decoration would take the control away exactly when the field is wrong.
   */
  const resolvedTrailingIcon =
    trailingIcon ?? (hasError ? <Icon name="alert-circle" size={ERROR_ICON_SIZE} /> : null);

  // A plain-text addon (string/number) gets its own padding; a control addon fills the segment.
  const isText = (node: ReactNode) => typeof node === 'string' || typeof node === 'number';

  return (
    <div className={classNames(styles.field, className)}>
      {label != null && (
        <label htmlFor={inputId} className={classNames(styles.label, hideLabel && styles.srOnly)}>
          <Typography as="span" variant="text-sm" weight="medium">
            {label}
          </Typography>
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <div
        className={classNames(
          styles.box,
          styles[size],
          hasError && styles.error,
          disabled && styles.disabled,
          leadingAddon != null && styles.hasLeading,
          trailingAddon != null && styles.hasTrailing,
        )}
      >
        {leadingAddon != null && (
          <div
            className={classNames(
              styles.segment,
              styles.segmentLeading,
              isText(leadingAddon) && styles.segmentPadded,
            )}
          >
            {leadingAddon}
          </div>
        )}

        <div className={styles.inputCell}>
          {prefix != null && <span className={styles.affix}>{prefix}</span>}
          {leadingIcon != null && <span className={styles.icon}>{leadingIcon}</span>}
          <input
            {...inputProps}
            id={inputId}
            ref={ref}
            required={required}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={message != null ? messageId : undefined}
            className={classNames(styles.input, inputClassName)}
          />
          {resolvedTrailingIcon != null && (
            <span
              className={classNames(
                styles.icon,
                trailingIcon == null && hasError && styles.errorIcon,
              )}
            >
              {resolvedTrailingIcon}
            </span>
          )}
          {suffix != null && <span className={styles.affix}>{suffix}</span>}
        </div>

        {trailingAddon != null && (
          <div
            className={classNames(
              styles.segment,
              styles.segmentTrailing,
              isText(trailingAddon) && styles.segmentPadded,
            )}
          >
            {trailingAddon}
          </div>
        )}
      </div>

      {message != null && (
        <Typography
          as="p"
          variant="text-sm"
          id={messageId}
          className={classNames(hasError ? styles.errorText : styles.hint)}
        >
          {message}
        </Typography>
      )}
    </div>
  );
});
